import React, { useState, useEffect, useRef } from 'react';
import './ModernToc.css';

/**
 * ModernToc - A reusable, responsive, sticky Table of Contents component.
 * 
 * @param {Array} data - Array of groups: [{ id: 'group-id', title: 'Group Title', items: [{ id: 'item-id', title: 'Item Title' }] }]
 * @param {string} title - The title of the TOC (default: 'Sommaire')
 * @param {string} ariaLabel - Accessibility label
 * @param {number} topMargin - Extra space below the navbar (default: 14)
 * @param {Array} boundarySelectors - Selectors of elements where TOC should stop (default: ['footer', '.map-banner'])
 */
const ModernToc = ({
    data = [],
    title = "Sommaire",
    ariaLabel = "Table des matières",
    topMargin = 14,
    boundarySelectors = ['footer', '.map-banner']
}) => {
    const [activeId, setActiveId] = useState('');
    const [isFixed, setIsFixed] = useState(false);
    const [box, setBox] = useState({ left: 0, width: 0 });
    const [maxHeight, setMaxHeight] = useState(0);
    const [useBottom, setUseBottom] = useState(false);
    const [bottomOffset, setBottomOffset] = useState(16);

    const tocRef = useRef(null);
    const originalTopRef = useRef(null);
    const headerHeightRef = useRef(84);
    const tocHeightRef = useRef(0);

    // 1. ScrollSpy - Detect active section
    useEffect(() => {
        if (!data.length) return;

        const allIds = data.flatMap(group => [
            group.id,
            ...(group.items ? group.items.map(i => i.id) : [])
        ]).filter(Boolean);

        const elements = allIds.map(id => document.getElementById(id)).filter(Boolean);
        if (!elements.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter(e => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) setActiveId(visible[0].target.id);
            },
            { root: null, rootMargin: '-140px 0px -50% 0px', threshold: [0.1, 0.5, 0.9] }
        );

        elements.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, [data]);

    // 2. Sticky Logic
    useEffect(() => {
        const aside = tocRef.current;
        if (!aside) return;

        const measure = () => {
            const rect = aside.getBoundingClientRect();
            // We only set originalTop once to know where it started
            if (originalTopRef.current === null) {
                originalTopRef.current = rect.top + window.scrollY;
            }
            setBox({ left: rect.left + window.scrollX, width: rect.width });
            tocHeightRef.current = aside.offsetHeight;
        };

        const onScroll = () => {
            const header = document.querySelector('.header');
            const hHeight = header?.offsetHeight || 84;
            headerHeightRef.current = hHeight;

            // Threshold: when scroll reaches original position minus navbar height
            if (originalTopRef.current !== null) {
                setIsFixed(window.scrollY >= originalTopRef.current - hHeight);
            }

            // Boundary detection (stop before footer)
            const boundaries = boundarySelectors.map(s => document.querySelector(s)).filter(Boolean);
            const boundaryTop = boundaries.length > 0
                ? Math.min(...boundaries.map(el => el.getBoundingClientRect().top))
                : Number.POSITIVE_INFINITY;

            const viewportHeight = window.innerHeight;
            const fixedTopLevel = hHeight + topMargin;
            const safeBottomMargin = 40; // General space from the bottom of the viewport/boundary

            // Available height for the TOC, accounting for both top and bottom safe zones
            const available = Math.min(viewportHeight - safeBottomMargin, boundaryTop - safeBottomMargin) - fixedTopLevel;
            setMaxHeight(Math.max(180, available));

            // Logic to "park" the TOC at the bottom when it hits the boundary
            if (tocHeightRef.current) {
                const currentTocHeight = Math.min(tocHeightRef.current, available);
                if (fixedTopLevel + currentTocHeight + safeBottomMargin >= boundaryTop) {
                    const distFromViewportBottom = Math.max(0, viewportHeight - boundaryTop);
                    setUseBottom(true);
                    setBottomOffset(distFromViewportBottom + safeBottomMargin);
                } else {
                    setUseBottom(false);
                    setBottomOffset(safeBottomMargin);
                }
            }
        };

        // Initial delay to ensure everything is rendered before measuring
        const timeoutId = setTimeout(() => {
            measure();
            onScroll();
        }, 200);

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', () => {
            originalTopRef.current = null; // Re-measure on resize
            measure();
            onScroll();
        });

        return () => {
            window.removeEventListener('scroll', onScroll);
            clearTimeout(timeoutId);
        };
    }, [data, topMargin, boundarySelectors]);

    return (
        <div className="modern-toc-wrapper">
            {/* Placeholder to prevent layout jump when becoming fixed */}
            {isFixed && <div aria-hidden="true" style={{ height: tocHeightRef.current }} />}

            <aside
                ref={tocRef}
                className={`modern-toc ${isFixed ? 'is-fixed' : ''}`}
                aria-label={ariaLabel}
                style={isFixed ? {
                    position: 'fixed',
                    left: `${box.left}px`,
                    width: `${box.width}px`,
                    maxHeight: `${maxHeight}px`,
                    zIndex: 50,
                    ...(useBottom
                        ? { bottom: `${bottomOffset}px`, top: 'auto' }
                        : { top: `${headerHeightRef.current + topMargin}px`, bottom: 'auto' }
                    )
                } : { alignSelf: 'start' }}
            >
                <div className="modern-toc-content">
                    <h4 className="modern-toc-title">{title}</h4>
                    <nav className="modern-toc-nav">
                        {data.map(group => (
                            <div key={group.id} className="toc-group">
                                <a
                                    href={`#${group.id}`}
                                    className={`toc-group-link ${activeId === group.id ? 'active' : ''}`}
                                >
                                    {group.title}
                                </a>
                                {group.items && group.items.length > 0 && (
                                    <ul className="toc-items">
                                        {group.items.map(item => (
                                            <li key={item.id}>
                                                <a
                                                    href={`#${item.id}`}
                                                    className={activeId === item.id ? 'active' : ''}
                                                >
                                                    {item.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </nav>
                </div>
            </aside>
        </div>
    );
};

export default ModernToc;
